import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

const ADMIN_EMAIL = "rikicaceras@gmail.com";
const CATEGORIES: Database["public"]["Enums"]["news_category"][] = [
  "europe",
  "primera",
  "rest_of_world",
  "transfers",
  "national_team",
  "articles",
  "interviews",
  "video_games",
  "scandals",
];

export const Route = createFileRoute("/admin")({
  ssr: false,
  head: () => ({ meta: [{ title: "Admin | Argentinian.Soccer" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: AdminPage,
});

type Article = Database["public"]["Tables"]["news_articles"]["Row"];

function AdminPage() {
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setEmail(data.user?.email ?? null);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setEmail(s?.user?.email ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  if (!email) return <LoginBox />;
  if (email.toLowerCase() !== ADMIN_EMAIL)
    return (
      <div className="mx-auto max-w-md p-8 text-center">
        <p className="text-sm">Signed in as {email}. Not authorized.</p>
        <button
          onClick={() => supabase.auth.signOut()}
          className="mt-4 rounded bg-primary px-4 py-2 text-sm text-primary-foreground"
        >
          Sign out
        </button>
      </div>
    );
  return <AdminConsole />;
}

function LoginBox() {
  const [loading, setLoading] = useState(false);
  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: String(fd.get("email")),
      password: String(fd.get("password")),
    });
    setLoading(false);
    if (error) toast.error(error.message);
  }
  return (
    <div className="mx-auto max-w-sm p-8">
      <h1 className="mb-4 text-xl font-semibold">Admin Login</h1>
      <form onSubmit={onSubmit} className="space-y-3">
        <input name="email" type="email" required placeholder="Email" className="w-full rounded border border-border bg-background px-3 py-2 text-sm" />
        <input name="password" type="password" required placeholder="Password" className="w-full rounded border border-border bg-background px-3 py-2 text-sm" />
        <button disabled={loading} className="w-full rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">
          {loading ? "…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function emptyDraft(): Partial<Article> {
  return {
    slug: "",
    title: "",
    excerpt: "",
    body: "",
    category: "national_team",
    author: "",
    published_at: new Date().toISOString(),
    cover_image: "",
  };
}

function AdminConsole() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [selected, setSelected] = useState<Partial<Article> | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const { data, error } = await supabase
      .from("news_articles")
      .select("*")
      .order("published_at", { ascending: false });
    if (error) toast.error(error.message);
    else setArticles(data ?? []);
  }
  useEffect(() => {
    refresh();
  }, []);

  const isNew = useMemo(() => selected && !selected.id, [selected]);

  async function save() {
    if (!selected) return;
    setBusy(true);
    const payload = {
      slug: selected.slug!,
      title: selected.title!,
      excerpt: selected.excerpt ?? null,
      body: selected.body ?? null,
      category: selected.category!,
      author: selected.author ?? null,
      published_at: selected.published_at!,
      cover_image: selected.cover_image ?? null,
    };
    const q = isNew
      ? supabase.from("news_articles").insert(payload).select().single()
      : supabase.from("news_articles").update(payload).eq("id", selected.id!).select().single();
    const { data, error } = await q;
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Saved");
    setSelected(data);
    refresh();
  }

  async function remove() {
    if (!selected?.id) return;
    if (!confirm(`Delete "${selected.title}"?`)) return;
    setBusy(true);
    const { error } = await supabase.from("news_articles").delete().eq("id", selected.id);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    setSelected(null);
    refresh();
  }

  return (
    <div className="mx-auto grid max-w-6xl gap-6 p-6 md:grid-cols-[320px_1fr]">
      <aside className="rounded border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border p-3">
          <h2 className="text-sm font-semibold">Articles ({articles.length})</h2>
          <button
            onClick={() => setSelected(emptyDraft())}
            className="rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground"
          >
            + New
          </button>
        </div>
        <ul className="max-h-[70vh] overflow-y-auto text-sm">
          {articles.map((a) => (
            <li key={a.id}>
              <button
                onClick={() => setSelected(a)}
                className={`block w-full border-b border-border px-3 py-2 text-left hover:bg-muted ${selected?.id === a.id ? "bg-muted" : ""}`}
              >
                <div className="line-clamp-1 font-medium">{a.title}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {new Date(a.published_at).toISOString().slice(0, 10)} · {a.category}
                </div>
              </button>
            </li>
          ))}
        </ul>
        <div className="border-t border-border p-3">
          <button onClick={() => supabase.auth.signOut()} className="text-xs text-muted-foreground underline">
            Sign out
          </button>
        </div>
      </aside>

      <section className="rounded border border-border bg-card p-4">
        {!selected ? (
          <p className="text-sm text-muted-foreground">Select an article or create a new one.</p>
        ) : (
          <div className="space-y-3">
            <Field label="Title">
              <input
                value={selected.title ?? ""}
                onChange={(e) => setSelected({ ...selected, title: e.target.value })}
                className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Slug">
              <input
                value={selected.slug ?? ""}
                onChange={(e) => setSelected({ ...selected, slug: e.target.value })}
                className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
              />
            </Field>
            <div className="grid gap-3 md:grid-cols-3">
              <Field label="Category">
                <select
                  value={selected.category ?? "national_team"}
                  onChange={(e) => setSelected({ ...selected, category: e.target.value as Article["category"] })}
                  className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Author">
                <input
                  value={selected.author ?? ""}
                  onChange={(e) => setSelected({ ...selected, author: e.target.value })}
                  className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
                />
              </Field>
              <Field label="Published at (ISO)">
                <input
                  value={selected.published_at ?? ""}
                  onChange={(e) => setSelected({ ...selected, published_at: e.target.value })}
                  className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
                />
              </Field>
            </div>
            <Field label="Cover image URL">
              <input
                value={selected.cover_image ?? ""}
                onChange={(e) => setSelected({ ...selected, cover_image: e.target.value })}
                className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Excerpt">
              <textarea
                value={selected.excerpt ?? ""}
                onChange={(e) => setSelected({ ...selected, excerpt: e.target.value })}
                rows={2}
                className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Body">
              <textarea
                value={selected.body ?? ""}
                onChange={(e) => setSelected({ ...selected, body: e.target.value })}
                rows={20}
                className="w-full rounded border border-border bg-background px-3 py-2 font-mono text-xs"
              />
            </Field>
            <div className="flex gap-2 pt-2">
              <button
                onClick={save}
                disabled={busy}
                className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
              >
                {busy ? "…" : isNew ? "Create" : "Save"}
              </button>
              {!isNew && (
                <button
                  onClick={remove}
                  disabled={busy}
                  className="rounded bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground disabled:opacity-50"
                >
                  Delete
                </button>
              )}
              <button onClick={() => setSelected(null)} className="rounded border border-border px-4 py-2 text-sm">
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      {children}
    </label>
  );
}
